import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIExpandableSection from 'UIComponents/accordion/UIExpandableSection';
import UIButton from 'UIComponents/button/UIButton';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import { H2, H5 } from 'UIComponents/elements/headings';
import UIButtonWrapper from 'UIComponents/layout/UIButtonWrapper';
import UIPanel from 'UIComponents/panel/UIPanel';
import UIPanelBody from 'UIComponents/panel/UIPanelBody';
import UIPanelFooter from 'UIComponents/panel/UIPanelFooter';
import UIPanelHeader from 'UIComponents/panel/UIPanelHeader';
import EnrollmentStep from 'automation-ui-canvas/containers/review/EnrollmentStep';
import SettingsStep from 'automation-ui-canvas/containers/review/SettingsStep';
import { getExistingTriggerType } from 'automation-ui-enrollment/utils/EnrollmentDeciders';
import DetailsStep from 'automation-ui-canvas/containers/review/DetailsStep';
import ConnectionsStep from 'automation-ui-canvas/containers/review/ConnectionsStep';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import { useCallback, useEffect, useState, useRef } from 'react';
import { defaultErrorHandlingForUpdateFlow, useUpdateFlow } from 'automation-ui-canvas/queries/flowQueries';
import { useTrackEvent } from 'automation-ui-usage-tracking/usageTrackingHooks';
import * as ActionTypes from 'automation-ui-action-features/constants/ActionTypes';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import { useDispatch } from 'react-redux';
import { bulkEnrollByListId, createBulkEnrollment } from 'automation-ui-canvas/actions/FlowActions';
import { BULK_ENROLL_FROM_LIST_FILTERS } from 'automation-ui-enrollment/queries/FlowEnrollmentQueries';
import { getObjectTypeId, getEnrollmentList, getSourceApp } from 'automation-ui-canvas/flow/records/Flow/flowUtils';
import { useDataFetchingClient } from 'data-fetching-client';
import { addAlert } from 'automation-ui-canvas/actions/MessageActions';
import * as AlertTypes from 'automation-ui-canvas/constants/AlertTypes';
import { Map as ImmutableMap } from 'immutable';
import * as UsageTrackingScreens from 'automation-ui-usage-tracking/UsageTrackingScreens';
import { trackPageView } from 'automation-ui-canvas/utils/trackingUtils';
import { initialEnrollmentState } from 'automation-ui-canvas/components/flow-header/menu-bar/menuOptions/constants/EnrollExistingState';
import { NavMarker } from 'react-rhumb';
import { useUpdateNameAndDescription } from 'automation-ui-management/components/WorkflowNameWithDescription/nameAndDescriptionHooks';
import { markActionComplete } from 'user-context/onboarding';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
// We have an interest in tracking the usage of these actions alongside our activation pipeline
// https://docs.google.com/document/d/1Luy3vMwokdLtubKCzdmgBI6DPJvG5_4KQgmTd453hi8/edit?usp=sharing
export const ACTION_TYPE_COUNTS_TO_TRACK = [ActionTypes.AUTOMATION_EXTENSION, ActionTypes.DELAY, ActionTypes.EMAIL_NOTIFICATION, ActionTypes.EMAIL, ActionTypes.LIST_BRANCH, ActionTypes.SET_PROPERTY, ActionTypes.TASK, ActionTypes.WEBHOOK, ActionTypes.FORMULA];
const ReviewSteps = [{
  id: 'enrollment',
  getTitle: () => I18n.text('platformReview.v2.enrollmentStep.title'),
  Component: EnrollmentStep
}, {
  id: 'settings',
  getTitle: () => I18n.text('platformReview.v2.settingsStep.title'),
  Component: SettingsStep
}, {
  id: 'connections',
  getTitle: () => I18n.text('platformReview.v2.connectionsStep.title'),
  Component: ConnectionsStep
}, {
  id: 'details',
  getTitle: () => I18n.text('platformReview.v2.detailsStep.title'),
  Component: DetailsStep
}];
const ReviewSidebar = ({
  flow,
  modalState,
  setModalState,
  onClose
}) => {
  const currentStep = modalState.get('currentStep') || 0;
  const enrollExistingState = modalState.get('enrollExistingState');
  const [isSaving, setIsSaving] = useState(false);
  const isEnrollExistingRequired = getExistingTriggerType(flow.toHybridFlowInterface()) === 'LIST';
  const isEnrollExistingMissing = (enrollExistingState === null || enrollExistingState === void 0 ? void 0 : enrollExistingState.shouldEnrollExistingFromListTriggers) === null || (enrollExistingState === null || enrollExistingState === void 0 ? void 0 : enrollExistingState.shouldEnrollExistingFromListTriggers) === undefined;
  const isMountedRef = useRef(true);
  useEffect(() => {
    // Mark as mounted
    isMountedRef.current = true;

    // Cleanup on unmount
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  const isLastStep = currentStep === ReviewSteps.length - 1;
  const isNextDisabled = isEnrollExistingRequired && isEnrollExistingMissing;
  const showSkipButton = !isNextDisabled && !isLastStep;
  const sourceApp = getSourceApp(flow);
  const numberOfActions = flow.getActions().size;
  const templateId = flow.getTemplateId();
  const trackEvent = useTrackEvent();
  const dispatch = useDispatch();
  const dataFetchingClient = useDataFetchingClient();
  const [updateFlow] = useUpdateFlow();
  const prevTriggerType = modalState.get('triggerType');
  const enrollmentTrigger = getExistingTriggerType(flow.toHybridFlowInterface());
  const H5Title = ({
    titleText
  }) => {
    return /*#__PURE__*/_jsx(H5, {
      className: "m-top-3",
      children: titleText
    });
  };
  useEffect(() => {
    trackPageView(UsageTrackingScreens.PLATFORM_REVIEW);
  }, []);

  /* reset enroll existing if trigger changes */
  useEffect(() => {
    if (prevTriggerType !== enrollmentTrigger) {
      setModalState(prev => prev.set('triggerType', enrollmentTrigger).set('enrollExistingState', initialEnrollmentState).set('currentStep', 0));
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [enrollmentTrigger]);
  const handleEnrollExisting = useCallback(() => {
    if (enrollExistingState) {
      // Only send bulk enrollment requests for platform workflows
      // Context: https://hubspot.slack.com/archives/C013E449G10/p1650459354564329
      if (enrollExistingState.shouldEnrollExistingFromListTriggers) {
        const enrollmentList = getEnrollmentList(flow);
        if (enrollmentList) {
          return dispatch(bulkEnrollByListId({
            flowId: flow.flowId,
            listId: enrollmentList.get('listId')
          }));
        }
      }

      // Enroll existing redesigned:
      if (enrollExistingState.listId) {
        return dispatch(bulkEnrollByListId({
          flowId: flow.flowId,
          listId: enrollExistingState.listId
        }));
      }
      const objectTypeId = getObjectTypeId(flow);
      if (enrollExistingState.selectedObjects && enrollExistingState.selectedObjects.length !== 0) {
        return dispatch(createBulkEnrollment({
          flowId: flow.flowId,
          objectTypeId,
          selectedIds: enrollExistingState.selectedObjects
        }, () => {}, () => {}));
      }
      if (enrollExistingState.filterBranch) {
        // we call the query directly here because otherwise the component unmounts
        // before the call is made (see: https://hubspot.slack.com/archives/C4ABAB8CR/p1687402083853889?thread_ts=1687382230.741039&cid=C4ABAB8CR)
        return dataFetchingClient.query({
          query: BULK_ENROLL_FROM_LIST_FILTERS,
          variables: {
            flowId: flow.flowId,
            objectType: objectTypeId,
            filterBranch: enrollExistingState.filterBranch
          }
        }).catch(() => {
          dispatch(addAlert(AlertTypes.DEFAULT));
        });
      }
    }
  }, [dataFetchingClient, dispatch, flow, enrollExistingState]);
  const {
    updateCrmObject
  } = useUpdateNameAndDescription({
    flowId: flow.flowId
  });
  const saveAiFlowDescription = useCallback(generatedDescription => {
    updateCrmObject({
      variables: {
        flowId: flow.flowId,
        description: generatedDescription
      }
    }).catch(error => {
      reportError({
        error
      });
    });
    const extraJSONData = {
      description: true,
      name: false,
      wasGeneratedDescriptionCustomized: false,
      withGeneratedDescription: true
    };
    trackEvent(Trackables.SAVED_WORKFLOW_NAME_AND_DESCRIPTION_MODAL, extraJSONData);
  }, [updateCrmObject, trackEvent, flow.flowId]);
  const handleActivation = useCallback(() => {
    setIsSaving(true);
    // To prevent creating multiple unique properties for the "active workflow" event,
    // we fire this event once for every action type so we can reused properties.
    // https://git.hubteam.com/HubSpot/WorkflowsReactUI/pull/4473#discussion_r1180926
    ACTION_TYPE_COUNTS_TO_TRACK.forEach(actionType => {
      const count = flow.getNumActionsOfType(actionType);
      trackEvent(Trackables.ACTION_TYPE_COUNT, {
        actionType,
        count,
        numberOfActions,
        sourceApp,
        templateId
      });
    });
    const aiFlowDescription = modalState.get('aiFlowDescription');
    const aiFlowName = modalState.get('aiFlowName');
    if (aiFlowDescription) {
      saveAiFlowDescription(aiFlowDescription);
    }
    let enabledFlow = flow.set('isEnabled', true);
    if (aiFlowName) {
      enabledFlow = enabledFlow.set('name', aiFlowName);
    }
    updateFlow({
      variables: {
        sourceApp: sourceApp,
        flow: enabledFlow
      }
    }).then(() => {
      void handleEnrollExisting();
      setModalState(ImmutableMap());
      markActionComplete('create-workflow').catch(() => {
        // Suppress errors in app @see https://git.hubteam.com/HubSpot/settings-ui-branding/pull/1491#discussion_r4010584
      });
      onClose();
    }).finally(() => {
      setIsSaving(false);
    }).catch(error => {
      defaultErrorHandlingForUpdateFlow({
        error,
        dispatch
      });
    });
  }, [flow, numberOfActions, sourceApp, templateId, trackEvent, updateFlow, dispatch, onClose, handleEnrollExisting, setModalState, saveAiFlowDescription, modalState]);
  const safeSetModalState = useCallback(nextState => {
    if (isMountedRef.current) {
      setModalState(nextState);
    }
  }, [setModalState]);
  return /*#__PURE__*/_jsxs(UIPanel, {
    width: 550,
    "data-test-id": "flow-review-page",
    children: [/*#__PURE__*/_jsx(UIDialogCloseButton, {
      onClick: onClose
    }), /*#__PURE__*/_jsx(UIPanelHeader, {
      children: /*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "platformReview.v2.title"
        })
      })
    }), /*#__PURE__*/_jsxs(UIPanelBody, {
      children: [/*#__PURE__*/_jsx(NavMarker, {
        name: "FLOW_REVIEW"
      }), ReviewSteps.map((step, idx) => {
        return /*#__PURE__*/_jsx(UIExpandableSection, {
          disabled: currentStep < idx,
          contentClassName: "p-x-8 m-x-0",
          open: currentStep === idx,
          onClick: () => {
            /* prevent clicks from leaking through other panels and triggering state updates. These state updates could overwrite whats happening in the other panel (i.e manual enrollment panel) */
            if (currentStep !== idx) {
              setModalState(prev => prev.set('currentStep', idx));
            }
          },
          Caret: 'div',
          Title: H5Title,
          titleText: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "platformReview.v2.step",
            options: {
              stepNumber: idx + 1,
              title: step.getTitle()
            }
          }),
          children: /*#__PURE__*/_jsx(step.Component, {
            flow: flow,
            modalState: modalState,
            setModalState: safeSetModalState
          })
        }, step.id);
      })]
    }), /*#__PURE__*/_jsxs(UIPanelFooter, {
      align: "between",
      children: [showSkipButton ? /*#__PURE__*/_jsx(UIButton, {
        "data-test-id": "review-sidebar-skip",
        use: "link",
        onClick: () => {
          trackEvent(Trackables.REVIEW_WORKFLOW_INTERACTION, {
            subAction: 'skip',
            subscreen: ReviewSteps[currentStep].id
          });
          setModalState(prev => prev.set('currentStep', ReviewSteps.length - 1));
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "platformReview.v2.skip"
        })
      }) : /*#__PURE__*/_jsx("div", {}), /*#__PURE__*/_jsxs(UIButtonWrapper, {
        children: [currentStep !== 0 && /*#__PURE__*/_jsx(UIButton, {
          use: "secondary",
          onClick: () => {
            trackEvent(Trackables.REVIEW_WORKFLOW_INTERACTION, {
              subAction: 'back',
              subscreen: ReviewSteps[currentStep].id
            });
            setModalState(prev => prev.set('currentStep', Math.max(0, currentStep - 1)));
          },
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "platformReview.v2.back"
          })
        }), /*#__PURE__*/_jsx(UITooltip, {
          disabled: !isNextDisabled,
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "platformReview.v2.validation.enrollExisting"
          }),
          children: isLastStep ? /*#__PURE__*/_jsx(UILoadingButton, {
            "data-test-id": "review-sidebar-turn-on",
            use: "primary",
            disabled: isNextDisabled,
            loading: isSaving,
            onClick: () => {
              var _flow$enrollmentCrite;
              if (((_flow$enrollmentCrite = flow.enrollmentCriteria) === null || _flow$enrollmentCrite === void 0 ? void 0 : _flow$enrollmentCrite.triggerType) === 'DATASET') {
                var _flow$enrollmentCrite2;
                trackEvent(Trackables.ACTIVATED_DATASET_WORKFLOW, {
                  assetType: 'dataset',
                  assetId: (_flow$enrollmentCrite2 = flow.enrollmentCriteria) === null || _flow$enrollmentCrite2 === void 0 ? void 0 : _flow$enrollmentCrite2.datasetId
                });
              }
              trackEvent(Trackables.REVIEW_WORKFLOW_INTERACTION, {
                subAction: 'activate',
                subscreen: ReviewSteps[currentStep].id
              });
              handleActivation();
            },
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "platformReview.v2.turnOn"
            })
          }) : /*#__PURE__*/_jsx(UIButton, {
            use: "primary",
            disabled: isNextDisabled,
            onClick: () => {
              trackEvent(Trackables.REVIEW_WORKFLOW_INTERACTION, {
                subAction: 'next',
                subscreen: ReviewSteps[currentStep].id
              });
              setModalState(prev => prev.set('currentStep', Math.min(ReviewSteps.length - 1, currentStep + 1)));
            },
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "platformReview.v2.next"
            })
          })
        })]
      })]
    })]
  });
};
export default ReviewSidebar;